/**
 * useIntakeDrafts — fetches the current user's open intake drafts and
 * provides an optimistic `discard` action with rollback on failure.
 *
 * Three-state (loading / empty / error) per ui-ux-pro-max rules.
 * Data-fetching stays in this hook — no inline ApiHelper calls in components.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, intake } from '@/helpers/ApiHelper';
import type { IntakeDraftSummary } from '@/types/intake';

export interface UseIntakeDrafts {
  drafts: IntakeDraftSummary[] | null; // null = still loading
  error: string | null;
  /** Re-fetch the draft list. */
  refetch: () => Promise<void>;
  /**
   * Optimistically remove the draft from the list, then call DELETE on the
   * server. On failure: rollback the list and return the error message.
   */
  discard: (id: string) => Promise<string | null>;
}

// Bounded client backoff for GET retries (idempotent, network/5xx/429 only).
const MAX_GET_RETRIES = 2;
const RETRY_DELAYS_MS = [300, 900];

async function fetchWithRetry(): Promise<IntakeDraftSummary[]> {
  let lastError: Error = new Error('Unknown error');
  for (let attempt = 0; attempt <= MAX_GET_RETRIES; attempt++) {
    try {
      const resp = await intake.listDrafts();
      return resp.results;
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e));
      const status = e instanceof ApiError ? e.status : 0;
      // Only retry on network/5xx/429 — not on 4xx (auth, not-found).
      const isRetriable = status === 0 || status === 429 || status >= 500;
      if (!isRetriable || attempt === MAX_GET_RETRIES) throw lastError;
      await new Promise<void>((res) =>
        setTimeout(res, RETRY_DELAYS_MS[attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]),
      );
    }
  }
  throw lastError;
}

export function useIntakeDrafts(): UseIntakeDrafts {
  const [drafts, setDrafts] = useState<IntakeDraftSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const results = await fetchWithRetry();
      setDrafts(results);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load drafts');
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const discard = useCallback(
    async (id: string): Promise<string | null> => {
      // Optimistic remove.
      const prev = drafts;
      setDrafts((d) => (d === null ? null : d.filter((draft) => draft.id !== id)));
      try {
        await intake.discard(id);
        return null;
      } catch (e) {
        // Rollback on failure.
        setDrafts(prev);
        return e instanceof ApiError ? e.detail : 'Failed to discard draft';
      }
    },
    [drafts],
  );

  return { drafts, error, refetch, discard };
}
